import * as React from 'react';

import {
  Button,
  DialogContent,
  DialogFooter,
  DialogOverlay,
  DialogTitle,
  Select,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideX } from 'lucide-react';

import { useAccountsForInviteQuery } from './_graphql/useAccountsForInviteQuery';
import { useInviteCustomerMutation } from './_graphql/useInviteCustomerMutation';

import {
  $closeInviteCustomerDialog,
  $inviteCustomerDialogOpen,
  $inviteCustomerFormData,
  $inviteCustomerFormValid,
  $updateInviteCustomerForm,
} from '~/modules/settings/_state/inviteCustomerDialog.state';
import { useTranslations } from '~/modules/settings/i18n/useTranslations';

export const InviteCustomerDialog = () => {
  const t = useTranslations();
  const toast = useToast();
  const queryClient = useQueryClient();

  const isOpen = useAtomValue($inviteCustomerDialogOpen);
  const formData = useAtomValue($inviteCustomerFormData);
  const isFormValid = useAtomValue($inviteCustomerFormValid);
  const updateForm = useSetAtom($updateInviteCustomerForm);
  const closeDialog = useSetAtom($closeInviteCustomerDialog);

  const { mutateAsync: inviteCustomer, isPending } =
    useInviteCustomerMutation();

  const { data: { accounts = [] } = {} } = useAccountsForInviteQuery({
    limit: 1000,
    offset: 0,
  });

  const accountOptions = React.useMemo(
    () => accounts.map((a) => ({ key: a.id, label: a.name })),
    [accounts],
  );

  const handleSave = React.useCallback(async () => {
    if (!isFormValid) return;

    try {
      await inviteCustomer({
        accountId: formData.accountId,
        email: formData.email.trim(),
      });
      toast.success(t.customerPortal.inviteCustomerDialog.messages.success);
      await queryClient.invalidateQueries({
        queryKey: ['settings.customerPortal.invitations'],
      });
      closeDialog();
    } catch (_error) {
      toast.error(t.customerPortal.inviteCustomerDialog.messages.error);
    }
  }, [
    isFormValid,
    formData,
    inviteCustomer,
    toast,
    t,
    queryClient,
    closeDialog,
  ]);

  const handleClose = () => {
    closeDialog();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isFormValid && !isPending) {
      handleSave();
    }
  };

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={(open) => !open && handleClose()}
      className="@InviteCustomerDialog"
    >
      <DialogContent className="min-w-[500px]" closeButton={false}>
        <div className="flex items-center justify-between px-4 pt-4">
          <DialogTitle className="text-xl font-bold">
            {t.customerPortal.inviteCustomerDialog.title}
          </DialogTitle>
          <button
            onClick={handleClose}
            className="flex items-center justify-center rounded bg-neutral/5 p-[3px]"
            aria-label={t.customerPortal.inviteCustomerDialog.actions.cancel}
          >
            <LucideX className="size-6 text-neutral/60" />
          </button>
        </div>

        <div className="flex flex-col gap-4 px-4 py-5">
          <Select
            aria-label={
              t.customerPortal.inviteCustomerDialog.fields.account.label
            }
            label={t.customerPortal.inviteCustomerDialog.fields.account.label}
            placeholder={
              t.customerPortal.inviteCustomerDialog.fields.account.placeholder
            }
            value={formData.accountId}
            onChange={(key) => updateForm({ accountId: key as string })}
            options={accountOptions}
          />
          <TextField
            aria-label={
              t.customerPortal.inviteCustomerDialog.fields.email.label
            }
            label={t.customerPortal.inviteCustomerDialog.fields.email.label}
            placeholder={
              t.customerPortal.inviteCustomerDialog.fields.email.placeholder
            }
            value={formData.email}
            onChange={(value) => updateForm({ email: value })}
            onKeyDown={handleKeyDown}
            type="email"
            isRequired
          />
        </div>

        <DialogFooter>
          <Button
            aria-label={t.customerPortal.inviteCustomerDialog.actions.cancel}
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isPending}
          >
            {t.customerPortal.inviteCustomerDialog.actions.cancel}
          </Button>
          <Button
            aria-label={
              t.customerPortal.inviteCustomerDialog.actions.sendInvite
            }
            variant="solid"
            color="primary"
            onPress={handleSave}
            isDisabled={!isFormValid || isPending}
            isLoading={isPending}
          >
            {t.customerPortal.inviteCustomerDialog.actions.sendInvite}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
